import { Button, ButtonProps } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTrigger } from '@/components/ui/dialog'
import { Drawer, DrawerContent, DrawerOverlay, DrawerTrigger } from '@/components/ui/drawer'
import { Separator } from '@/components/ui/separator'
import { getNoteBech32Id } from '@/lib/event'
import { resolveClientUrlPattern, resolveClientUrlPatternFromPointer } from '@/lib/client-handlers'
import { isSmallScreenAtom } from '@/atoms/screenSize'
import { useAtomValue } from 'jotai'
import { HugeiconsIcon } from '@hugeicons/react'
import { ArrowUpRight01Icon } from '@hugeicons/core-free-icons'
import { Event } from '@nostr/tools/wasm'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DialogTitle } from '@radix-ui/react-dialog'
import { AddressPointer, decode, EventPointer } from '@nostr/tools/nip19'

export default function ClientSelect({
  event,
  originalNoteId,
  ...props
}: ButtonProps & {
  event?: Event
  originalNoteId?: string
}) {
  const isSmallScreen = useAtomValue(isSmallScreenAtom)
  const [open, setOpen] = useState(false)
  const { t } = useTranslation()

  if (!originalNoteId && !event) return null

  let pointer: EventPointer | AddressPointer | undefined
  let code: string
  if (originalNoteId) {
    try {
      const { type, data } = decode(originalNoteId)
      if (type !== 'nevent' && type !== 'naddr') return null
      code = originalNoteId
      pointer = data
    } catch {
      /***/
    }
  }
  if (!pointer) {
    code = getNoteBech32Id(event!)
    const { data } = decode(code)
    pointer = data as EventPointer | AddressPointer
  }

  let kind: number | undefined
  if ('identifier' in pointer) {
    kind = pointer.kind
  } else {
    kind = pointer.kind
  }

  const handlerRegistry = window.hallway.universe?.clientHandlers
  const fallbackHandlers = handlerRegistry?.fallback ?? []
  const kindHandlers = handlerRegistry?.byKind?.[kind!] ?? []
  const supportedClients = [...kindHandlers, ...fallbackHandlers]

  function resolveClientUrl(pattern: string): string {
    if (event) return resolveClientUrlPattern(pattern, event)
    return resolveClientUrlPatternFromPointer(pattern, pointer!, code)
  }

  const content = (
    <div className="space-y-2">
      {supportedClients.map((client) => (
        <ClientSelectItem
          key={client.name}
          onClick={() => setOpen(false)}
          href={resolveClientUrl(client.urlPattern)}
          name={client.name}
        />
      ))}
      <Separator />
      <Button
        variant="ghost"
        className="w-full py-6 font-semibold"
        onClick={() => {
          navigator.clipboard.writeText(originalNoteId ?? getNoteBech32Id(event!))
          setOpen(false)
        }}
      >
        {t('Copy event ID')}
      </Button>
    </div>
  )

  const trigger = (
    <Button variant="outline" {...props}>
      <HugeiconsIcon icon={ArrowUpRight01Icon} /> {t('Open in another client')}
    </Button>
  )

  if (isSmallScreen) {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <Drawer open={open} onOpenChange={setOpen}>
          <DrawerTrigger asChild>{trigger}</DrawerTrigger>
          <DrawerOverlay
            onClick={(e) => {
              e.stopPropagation()
              setOpen(false)
            }}
          />
          <DrawerContent hideOverlay>{content}</DrawerContent>
        </Drawer>
      </div>
    )
  }

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogHeader className="hidden">
          <DialogTitle>Client Select</DialogTitle>
        </DialogHeader>
        <DialogTrigger asChild>{trigger}</DialogTrigger>
        <DialogContent className="px-8" onOpenAutoFocus={(e) => e.preventDefault()}>
          {content}
        </DialogContent>
      </Dialog>
    </div>
  )
}

function ClientSelectItem({
  onClick,
  href,
  name
}: {
  onClick: () => void
  href: string
  name: string
}) {
  return (
    <Button asChild variant="ghost" className="w-full py-6 font-semibold" onClick={onClick}>
      <a href={href} target="_blank" rel="noopener noreferrer">
        {name}
      </a>
    </Button>
  )
}
